import {
    isWorkflowOffset,
    MAX_WORKFLOW_OFFSET_DAYS,
    TaskNouns,
    TelecomPhoneLineId,
    WorkflowActionId,
    workflowOffsetMinutes,
} from "@sp-crm/core";
import {
    AdvancedSearchEntityType,
    WorkflowActionEntityInput,
    WorkflowEventType,
    WorkflowPayload,
    WorkflowStatus,
} from "generated/graphql";
import { CrmMode } from "util/crm-mode";
import {
    computeWorkflowDelay,
    entityTypeLabel,
    isTokenDelayRecipe,
} from "../manage-workflows-helper";

export interface WorkflowDelaySource {
    delay: number;
    delayRecipe: string | null;
}

export type WorkflowActionTypename =
    | "WorkflowActionEmailEntity"
    | "WorkflowActionTaskEntity"
    | "WorkflowActionSmsEntity";

export interface WorkflowChipSource extends WorkflowDelaySource {
    trigger: { eventType: WorkflowEventType };
    actions: ReadonlyArray<{
        __typename: WorkflowActionTypename;
        delayRecipe: string | null;
    }>;
}

const WAITLESS_EVENT_TYPES: WorkflowEventType[] = [
    WorkflowEventType.EmailOpened,
    WorkflowEventType.Schedule,
];

export type WizardActionKind = "email" | "task" | "sms";

export interface WorkflowActionChip {
    kind: WizardActionKind;
    delayLabel: string | null;
}

export function delayChipLabel(workflow: WorkflowDelaySource): string | null {
    if (isTokenDelayRecipe(workflow.delayRecipe)) {
        return null;
    }
    const [delay, unit] = computeWorkflowDelay(workflow);
    if (delay === 0) {
        return "Immediately";
    }
    return `+${delay}${unit}`;
}

export function workflowActionChips(workflow: WorkflowChipSource): WorkflowActionChip[] {
    const labelsDelay = WAITLESS_EVENT_TYPES.indexOf(workflow.trigger.eventType) === -1;
    return workflowActionTimings(workflow, null).map((timing, index) => ({
        kind: actionChipKind(workflow.actions[index].__typename),
        delayLabel: index === 0 && !labelsDelay ? null : delayChipLabel(timing),
    }));
}

const triggerTiming = (
    workflow: WorkflowChipSource,
    anchoredOffset: string | null,
): WorkflowDelaySource =>
    anchoredOffset !== null && isTokenDelayRecipe(workflow.delayRecipe)
        ? { delay: 0, delayRecipe: anchoredOffset }
        : { delay: workflow.delay, delayRecipe: workflow.delayRecipe };

export function workflowActionTimings(
    workflow: WorkflowChipSource,
    anchoredOffset: string | null,
): WorkflowDelaySource[] {
    return workflow.actions.map((action, index) =>
        index === 0
            ? triggerTiming(workflow, anchoredOffset)
            : { delay: 0, delayRecipe: action.delayRecipe },
    );
}

const MINUTES_PER_DAY = workflowOffsetMinutes(1, "d");
const DAYS_PER_WEEK = 7;
const WEEK_SUFFIX_MIN_DAYS = 14;

const timingMinutes = (timing: WorkflowDelaySource): number => {
    if (isTokenDelayRecipe(timing.delayRecipe)) {
        return 0;
    }
    const [delay, unit] = computeWorkflowDelay(timing);
    return workflowOffsetMinutes(delay, unit);
};

export function actionTimelineLabel(
    timings: readonly WorkflowDelaySource[],
    index: number,
): string | null {
    if (
        index >= timings.length ||
        (index === 0 && isTokenDelayRecipe(timings[0].delayRecipe))
    ) {
        return null;
    }
    const totalMinutes = timings
        .slice(0, index + 1)
        .reduce((total, timing) => total + timingMinutes(timing), 0);
    const days = Math.floor(totalMinutes / MINUTES_PER_DAY);
    const label = `Day ${days + 1}`;
    if (days < WEEK_SUFFIX_MIN_DAYS) {
        return label;
    }
    return `${label} · ~${Math.round(days / DAYS_PER_WEEK)} weeks after start`;
}

export function actionChipKind(typename: WorkflowActionTypename): WizardActionKind {
    switch (typename) {
        case "WorkflowActionTaskEntity":
            return "task";
        case "WorkflowActionSmsEntity":
            return "sms";
        case "WorkflowActionEmailEntity":
            return "email";
    }
}

export function actionKindLabel(kind: WizardActionKind, nouns: TaskNouns): string {
    switch (kind) {
        case "email":
            return "Email";
        case "sms":
            return "Text message";
        case "task":
            return nouns.titleSingular;
    }
}

export interface ActionKindChoice {
    key: string;
    title: string;
    description?: string;
}

export interface ActionKindSummary {
    kind: WizardActionKind;
    title: string;
    description: string | null;
}

export function actionKindSummary(
    kind: WizardActionKind,
    nouns: TaskNouns,
    choices: readonly ActionKindChoice[],
    selectedKey: string | null,
): ActionKindSummary {
    const choice = choices.find(candidate => candidate.key === selectedKey);
    if (!choice) {
        return { kind, title: actionKindLabel(kind, nouns), description: null };
    }
    return { kind, title: choice.title, description: choice.description ?? null };
}

export function actionSettingKey(base: string, suffix: string): string {
    return `${base}${suffix}`;
}

const CONTENT_SETTING_KEYS: Record<WizardActionKind, string> = {
    email: "emailTemplate",
    sms: "smsBody",
    task: "description",
};

const SETTING_TEMPLATE_PATTERN = new RegExp(
    `\\{\\{settings\\.(${Object.values(CONTENT_SETTING_KEYS).join("|")})(\\d*)\\}\\}`,
);

export function settingSuffixFromTemplate(
    kind: WizardActionKind,
    template: string,
): string {
    const match = template.match(SETTING_TEMPLATE_PATTERN);
    return match && match[1] === CONTENT_SETTING_KEYS[kind] ? match[2] : "";
}

export type WorkflowActionTemplateSource =
    | { __typename: "WorkflowActionEmailEntity"; subject: string }
    | { __typename: "WorkflowActionSmsEntity"; body: string }
    | { __typename: "WorkflowActionTaskEntity"; text: string };

export function workflowActionSettingSuffix(
    action: WorkflowActionTemplateSource,
): string {
    switch (action.__typename) {
        case "WorkflowActionEmailEntity":
            return settingSuffixFromTemplate("email", action.subject);
        case "WorkflowActionSmsEntity":
            return settingSuffixFromTemplate("sms", action.body);
        case "WorkflowActionTaskEntity":
            return settingSuffixFromTemplate("task", action.text);
    }
}

export function actionEntitySettingSuffix(action: WorkflowActionEntityInput): string {
    if (action.sms) {
        return settingSuffixFromTemplate("sms", action.sms.body);
    }
    if (action.email) {
        return settingSuffixFromTemplate("email", action.email.subject);
    }
    if (action.task) {
        return settingSuffixFromTemplate("task", action.task.text);
    }
    return "";
}

export function actionEntityId(
    action: WorkflowActionEntityInput,
): WorkflowActionId | null {
    return action.task?.id ?? action.sms?.id ?? action.email?.id ?? null;
}

export function actionEntityDelayRecipe(
    action: WorkflowActionEntityInput,
): string | null {
    return (
        action.task?.delayRecipe ??
        action.sms?.delayRecipe ??
        action.email?.delayRecipe ??
        null
    );
}

export function withActionEntityDelayRecipe(
    action: WorkflowActionEntityInput,
    delayRecipe: string,
): WorkflowActionEntityInput {
    if (action.task) {
        return { ...action, task: { ...action.task, delayRecipe } };
    }
    if (action.sms) {
        return { ...action, sms: { ...action.sms, delayRecipe } };
    }
    if (action.email) {
        return { ...action, email: { ...action.email, delayRecipe } };
    }
    return action;
}

const suffixOrdinal = (suffix: string): number =>
    suffix === "" ? 1 : parseInt(suffix, 10) || 1;

export function nextActionSettingSuffix(used: readonly string[]): string {
    const highest = used.reduce((max, suffix) => Math.max(max, suffixOrdinal(suffix)), 0);
    return highest === 0 ? "" : String(highest + 1);
}

const BASE_SETTING_KEYS: Record<WizardActionKind, string[]> = {
    email: ["recipients", "emailTemplate", "bccMyself"],
    sms: ["smsRecipients", "smsBody"],
    task: ["description", "dueDateDays", "assignedTo"],
};

export function actionSettingKeys(kind: WizardActionKind, suffix: string): string[] {
    return BASE_SETTING_KEYS[kind].map(base => actionSettingKey(base, suffix));
}

export interface ActionSettingsClaim {
    kind: WizardActionKind | null;
    suffix: string;
}

const claimKeys = (claim: ActionSettingsClaim): string[] =>
    claim.kind ? actionSettingKeys(claim.kind, claim.suffix) : [];

export function settingsForAction<T extends { key: string }>(
    settings: readonly T[],
    claims: readonly ActionSettingsClaim[],
    index: number,
): T[] {
    const claimed = claims.flatMap(claimKeys);
    const own = claims[index] ? claimKeys(claims[index]) : [];
    return settings.filter(
        setting =>
            own.includes(setting.key) || (index === 0 && !claimed.includes(setting.key)),
    );
}

export function settingActionPosition(
    claims: readonly ActionSettingsClaim[],
    key: string,
): number {
    const position = claims.findIndex(claim => claimKeys(claim).includes(key));
    return position === -1 ? 0 : position;
}

export function actionSettingLabel(
    label: string,
    position: number,
    actionCount: number,
): string {
    return actionCount > 1 && label ? `Action ${position + 1} · ${label}` : label;
}

export function isActionIntervalRecipe(recipe: string | null): boolean {
    return recipe !== null && isWorkflowOffset(recipe);
}

interface SmsActionGaps {
    needsPhoneLine: boolean;
    needsConsent: boolean;
}

type SmsGapMessages = Record<"both" | "phoneLine" | "consent", string>;

const PUBLISH_SMS_MESSAGES: SmsGapMessages = {
    both: "To publish, choose a sending phone number and confirm the consent acknowledgment on the Actions page.",
    phoneLine: "To publish, choose a sending phone number on the Actions page.",
    consent: "To publish, confirm the consent acknowledgment on the Actions page.",
};

const CONTINUE_SMS_MESSAGES: SmsGapMessages = {
    both: "Choose a sending phone number and confirm the consent acknowledgment to continue.",
    phoneLine: "Choose a sending phone number to continue.",
    consent: "Confirm the consent acknowledgment to continue.",
};

function smsActionGaps(action: WorkflowActionEntityInput): SmsActionGaps | null {
    if (!action.sms) {
        return null;
    }
    return {
        needsPhoneLine: !action.sms.fromPhoneLineId,
        needsConsent: !action.sms.consentAcknowledged,
    };
}

function smsGapMessage(gaps: SmsActionGaps, messages: SmsGapMessages): string | null {
    if (gaps.needsPhoneLine && gaps.needsConsent) {
        return messages.both;
    }
    if (gaps.needsPhoneLine) {
        return messages.phoneLine;
    }
    if (gaps.needsConsent) {
        return messages.consent;
    }
    return null;
}

const actionPrefix = (position: number, actionCount: number): string =>
    actionCount > 1 ? `Action ${position + 1}: ` : "";

function firstSmsBlocker(
    payload: WorkflowPayload,
    messages: SmsGapMessages,
): string | null {
    const actions = payload.actionEntities ?? [];
    for (let position = 0; position < actions.length; position++) {
        const gaps = smsActionGaps(actions[position]);
        const message = gaps ? smsGapMessage(gaps, messages) : null;
        if (message) {
            return `${actionPrefix(position, actions.length)}${message}`;
        }
    }
    return null;
}

function firstWaitBlocker(payload: WorkflowPayload): string | null {
    const actions = payload.actionEntities ?? [];
    for (let position = 1; position < actions.length; position++) {
        const recipe = actionEntityDelayRecipe(actions[position]);
        if (recipe !== null && !isActionIntervalRecipe(recipe)) {
            return `Action ${position + 1} needs a wait of at most ${MAX_WORKFLOW_OFFSET_DAYS} days after the previous action. Set it on the Actions page.`;
        }
    }
    return null;
}

export function workflowPublishBlocker(payload: WorkflowPayload): string | null {
    return firstSmsBlocker(payload, PUBLISH_SMS_MESSAGES) ?? firstWaitBlocker(payload);
}

export function actionContinueBlocker(payload: WorkflowPayload): string | null {
    return firstSmsBlocker(payload, CONTINUE_SMS_MESSAGES);
}

export interface SmsActionRequirements {
    fromPhoneLineId: TelecomPhoneLineId | null;
    consentAcknowledged: boolean;
}

export interface SmsPhoneLineAvailability {
    loaded: boolean;
    phoneLineCount: number;
}

export function smsActionBlocker(availability: SmsPhoneLineAvailability): string | null {
    if (!availability.loaded || availability.phoneLineCount > 0) {
        return null;
    }
    return "No active phone numbers are available in this workflow's regions. Claim or assign a phone number before setting up this text message.";
}

export type WizardSectionEditor =
    | "action-kind"
    | "email-sender"
    | "sms-sender"
    | "task-details"
    | "task-notification";

export type WizardSectionItem =
    | { type: "setting"; settingKey: string; label: "hidden" | "setting" }
    | { type: "editor"; editor: WizardSectionEditor };

export interface WizardActionSection {
    key: string;
    title: string;
    description: string | null;
    items: WizardSectionItem[];
}

export interface WizardSectionNouns {
    task: TaskNouns;
    entity: string;
}

export interface WizardActionSpec {
    kind: WizardActionKind | null;
    suffix: string;
    position: number;
}

const namedSetting = (settingKey: string): WizardSectionItem => ({
    type: "setting",
    settingKey,
    label: "hidden",
});

const labeledSetting = (settingKey: string): WizardSectionItem => ({
    type: "setting",
    settingKey,
    label: "setting",
});

const sectionEditor = (editor: WizardSectionEditor): WizardSectionItem => ({
    type: "editor",
    editor,
});

const actionKindSection = (position: number): WizardActionSection => ({
    key: "actionKind",
    title: "What should this action do?",
    description: null,
    items:
        position === 0
            ? [sectionEditor("action-kind"), labeledSetting("delay")]
            : [sectionEditor("action-kind")],
});

const emailSections = (suffix: string): WizardActionSection[] => [
    {
        key: "emailRecipients",
        title: "Who should receive the email?",
        description: null,
        items: [namedSetting(actionSettingKey("recipients", suffix))],
    },
    {
        key: "emailContent",
        title: "What should the email say?",
        description: null,
        items: [
            namedSetting(actionSettingKey("emailTemplate", suffix)),
            labeledSetting(actionSettingKey("bccMyself", suffix)),
        ],
    },
    {
        key: "emailSender",
        title: "Who should send the email?",
        description: null,
        items: [sectionEditor("email-sender")],
    },
];

const smsSections = (suffix: string): WizardActionSection[] => [
    {
        key: "smsRecipients",
        title: "Who should receive a text message?",
        description: null,
        items: [namedSetting(actionSettingKey("smsRecipients", suffix))],
    },
    {
        key: "smsContent",
        title: "What should the message say?",
        description: null,
        items: [namedSetting(actionSettingKey("smsBody", suffix))],
    },
    {
        key: "smsSender",
        title: "Which phone number should send the text message?",
        description: null,
        items: [sectionEditor("sms-sender")],
    },
];

const taskSections = (
    nouns: WizardSectionNouns,
    suffix: string,
): WizardActionSection[] => [
    {
        key: "taskDetails",
        title: `What should the ${nouns.task.singular} say?`,
        description: null,
        items: [
            namedSetting(actionSettingKey("description", suffix)),
            sectionEditor("task-details"),
        ],
    },
    {
        key: "taskDueDate",
        title: `When is the ${nouns.task.singular} due?`,
        description: "Days after the workflow runs.",
        items: [namedSetting(actionSettingKey("dueDateDays", suffix))],
    },
    {
        key: "taskAssignee",
        title: `Who should the ${nouns.task.singular} be assigned to?`,
        description: `Leave blank to use the ${nouns.entity}'s assigned user.`,
        items: [namedSetting(actionSettingKey("assignedTo", suffix))],
    },
    {
        key: "taskNotification",
        title: `How should we notify the ${nouns.task.singular} owner?`,
        description: null,
        items: [sectionEditor("task-notification")],
    },
];

const candidateSections = (
    action: WizardActionSpec,
    nouns: WizardSectionNouns,
): WizardActionSection[] => {
    switch (action.kind) {
        case "email":
            return [actionKindSection(action.position), ...emailSections(action.suffix)];
        case "sms":
            return [actionKindSection(action.position), ...smsSections(action.suffix)];
        case "task":
            return [
                actionKindSection(action.position),
                ...taskSections(nouns, action.suffix),
            ];
        case null:
            return [actionKindSection(action.position)];
    }
};

const claimedSettingKeys = (sections: WizardActionSection[]): string[] =>
    sections.flatMap(section =>
        section.items.flatMap(item => (item.type === "setting" ? [item.settingKey] : [])),
    );

export function wizardActionSections(
    action: WizardActionSpec,
    settingKeys: readonly string[],
    nouns: WizardSectionNouns,
): WizardActionSection[] {
    const candidates = candidateSections(action, nouns);
    const claimed = claimedSettingKeys(candidates);
    const sections = candidates
        .map(section => ({
            ...section,
            items: section.items.filter(
                item => item.type === "editor" || settingKeys.includes(item.settingKey),
            ),
        }))
        .filter(section => section.items.length > 0);
    const unclaimed = settingKeys.filter(key => !claimed.includes(key));
    if (unclaimed.length === 0 || action.position > 0) {
        return sections;
    }
    return [
        ...sections,
        {
            key: "otherSettings",
            title: "Other settings",
            description: null,
            items: unclaimed.map(labeledSetting),
        },
    ];
}

export function sectionNumber(
    position: number,
    index: number,
    actionCount: number,
): string {
    return actionCount > 1 ? `${position + 1}.${index + 1}` : `${index + 1}`;
}

export function wizardActionSettings<T extends { scope: string }>(
    settings: readonly T[],
): T[] {
    return settings.filter(s => s.scope === "tenant");
}

export type ActionTimingMode = "immediately" | "wait";

export function actionTimingMode(delay: number): ActionTimingMode {
    return delay === 0 ? "immediately" : "wait";
}

export function filterWorkflows<
    T extends {
        name: string;
        attachTo: AdvancedSearchEntityType;
        status: WorkflowStatus;
    },
>(
    workflows: readonly T[],
    entityTypeFilter: AdvancedSearchEntityType | "all",
    statusFilter: WorkflowStatus | "all",
    searchText: string,
): T[] {
    return workflows.filter(
        workflow =>
            (entityTypeFilter === "all" || workflow.attachTo === entityTypeFilter) &&
            (statusFilter === "all" || workflow.status === statusFilter) &&
            workflow.name.toLowerCase().includes(searchText.toLowerCase()),
    );
}

export function workflowsForEntityTypes<T extends { attachTo: AdvancedSearchEntityType }>(
    supportedEntityTypes: readonly AdvancedSearchEntityType[],
    workflows: readonly T[],
): T[] {
    return workflows.filter(workflow => supportedEntityTypes.includes(workflow.attachTo));
}

export function presentEntityTypes<T extends { attachTo: AdvancedSearchEntityType }>(
    supportedEntityTypes: readonly AdvancedSearchEntityType[],
    workflows: readonly T[],
): AdvancedSearchEntityType[] {
    return supportedEntityTypes.filter(entityType =>
        workflows.some(workflow => workflow.attachTo === entityType),
    );
}

export function sortWorkflowsByRecentlyUpdated<T extends { updatedAt: string }>(
    workflows: readonly T[],
): T[] {
    return [...workflows].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
}

export function workflowEntityTypeLabel(
    attachTo: AdvancedSearchEntityType,
    mode: CrmMode,
): string {
    switch (attachTo) {
        case AdvancedSearchEntityType.Client:
            return mode === "combined" ? "Client - Placement" : "Client";
        case AdvancedSearchEntityType.HomeCareLead:
            return mode === "combined" ? "Client - Home Care" : "Client";
        default:
            return entityTypeLabel(attachTo);
    }
}
